import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin,
  Star,
  Clock,
  Shield,
  Award
} from 'lucide-react';
import { useCategories } from '../hooks/useFirestoreServices';

export default function Footer() {
  const { categories } = useCategories();
  
  // Get first 6 active categories for footer
  const footerCategories = categories
    .filter(cat => cat.isActive !== false)
    .slice(0, 6)
    .map(cat => ({
      name: cat.name,
      id: cat.id,
      icon: cat.icon
    }));

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Refund Policy', href: '/refund' },
    { name: 'FAQ', href: '/faq' }
  ];

  const stats = [
    { icon: Star, value: '5,000+', label: 'Happy Customers' },
    { icon: Clock, value: '24/7', label: 'Service Available' },
    { icon: Shield, value: '100%', label: 'Service Guarantee' },
    { icon: Award, value: '1 Year', label: 'Experience' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Stats Section */}
      <div className="bg-primary-600 py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 mx-auto mb-2 text-primary-100" />
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-primary-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Company Info */}
          <div>
            <div className="text-2xl font-bold mb-4 text-white">
              RAPS<span className="text-secondary-400">APP</span>
            </div>
            <p className="text-gray-300 mb-4 leading-relaxed text-sm">
              Professional home services with 100% satisfaction guarantee.
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Contact</h3>
            <div className="space-y-3">
              <div className="flex items-start text-gray-300 text-sm">
                <Phone className="h-4 w-4 mr-2 text-primary-400 flex-shrink-0 mt-0.5" />
                <a href="tel:+917018401540" className="hover:text-primary-400 transition-colors">
                  +91 7018401540
                </a>
              </div>
              <div className="flex items-start text-gray-300 text-sm">
                <Mail className="h-4 w-4 mr-2 text-primary-400 flex-shrink-0 mt-0.5" />
                <a href="mailto:rapsappservice@gmail.com" className="hover:text-primary-400 transition-colors break-all">
                  rapsappservice@gmail.com
                </a>
              </div>
              <div className="flex items-start text-gray-300 text-sm">
                <MapPin className="h-4 w-4 mr-2 text-primary-400 flex-shrink-0 mt-0.5" />
                <span>Mandi, HP</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Categories</h3>
            <ul className="space-y-2">
              {footerCategories.map((category, index) => (
                <li key={index}>
                  <Link 
                    to={`/services?category=${category.id}`} 
                    className="text-gray-300 hover:text-primary-400 transition-colors text-sm flex items-center gap-1"
                  >
                    {category.icon && <span>{category.icon}</span>}
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors block text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Legal</h3>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors block text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Hours */}
          <div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold mb-3 text-white flex items-center text-base">
                <Clock className="h-5 w-5 mr-2 text-primary-400" />
                Hours
              </h3>
              <div className="text-sm text-gray-300 space-y-2">
                <div>
                  <p className="font-medium text-white">Mon - Sat</p>
                  <p className="text-xs">8 AM - 8 PM</p>
                </div>
                <div>
                  <p className="font-medium text-white">Sunday</p>
                  <p className="text-xs">10 AM - 6 PM</p>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <p className="text-primary-400 font-medium flex items-center text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    24/7 Emergency
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <p className="text-gray-400 text-sm">
              © 2025 RAPSAPP. All rights reserved. | Made with ❤️ in Apna Himachal
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
